import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Star, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { bnplServices } from '../data/mock';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = bnplServices.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Service Not Found</CardTitle>
            <CardDescription>The service you're looking for doesn't exist.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Service Hero */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl font-bold">{service.name}</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">{service.name}</h1>
            <p className="text-xl mb-6 opacity-90">{service.tagline}</p>
            <div className="flex items-center justify-center gap-4 mb-6">
              <Badge variant="secondary" className="bg-white/20 text-white text-base px-4 py-1">
                {service.type}
              </Badge>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold text-lg">{service.rating}</span>
                <span className="opacity-75">({service.reviewCount} reviews)</span>
              </div>
            </div>
            <a href={service.website} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-slate-100">
                Visit Official Website
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Products/Plans */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Available Products</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {service.products.map((product, idx) => (
                <Card key={idx} className="border-2 hover:border-blue-600 hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-2xl">{product.name}</CardTitle>
                      {product.noInterestOption && (
                        <Badge className="bg-green-100 text-green-700">Interest-Free</Badge>
                      )}
                    </div>
                    <CardDescription className="text-base">{product.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium text-slate-600">Fixed Fee</span>
                        <span className="font-bold text-slate-800">{product.fixedFee}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium text-slate-600">Interest Rate</span>
                        <span className="font-bold text-slate-800">{product.interestRate}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium text-slate-600">Late Fee</span>
                        <span className="font-bold text-slate-800">{product.lateFee}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium text-slate-600">Repayment Period</span>
                        <span className="font-bold text-slate-800">{product.repaymentPeriod}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="font-medium text-slate-600">Max Amount</span>
                        <span className="font-bold text-slate-800">{product.maxAmount}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Key Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Pros & Cons</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-green-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6" />
                    Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.pros.map((pro, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-red-700 flex items-center gap-2">
                    <AlertCircle className="h-6 w-6" />
                    Limitations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.cons.map((con, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{con}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Get Started with {service.name}?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Visit their official website to sign up and start using their service today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={service.website} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-slate-100">
                Visit {service.name}
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/compare">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Compare All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServicesOverview = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">All Services</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Australian BNPL Services</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Explore detailed information about each Buy Now Pay Later service available in Australia
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {bnplServices.map((service) => (
              <Card key={service.id} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-600">
                <CardHeader>
                  <div className="w-full h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg mb-4 flex items-center justify-center">
                    <span className="text-3xl font-bold text-slate-700">{service.name}</span>
                  </div>
                  <CardTitle className="text-2xl">{service.name}</CardTitle>
                  <CardDescription className="text-base">{service.tagline}</CardDescription>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="ml-1 font-semibold">{service.rating}</span>
                    </div>
                    <span className="text-sm text-slate-500">({service.reviewCount} reviews)</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <Badge className="mb-4" variant="outline">{service.type}</Badge>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-2">
                    <Link to={`/services/${service.id}`}>
                      <Button className="w-full">
                        View Details
                      </Button>
                    </Link>
                    <a href={service.website} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full" variant="outline">
                        Visit Website
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export { ServiceDetail, ServicesOverview };